import React from 'react';

const IndustrialConstructionLights = () => {
    // Image placeholder data - replace with actual image paths later
    const imagePlaceholders = {
        industrial: {
            highBay: 'assets/greenlightimages/highbayligt.webp',
            explosionprooflight: 'assets/greenlightimages/explosionprooflight.jpeg',
            ledtechimage: 'assets/greenlightimages/ledtechimage.jpeg',
            lowbayligt: 'assets/greenlightimages/lowbayligt.webp',
            streetlight: 'assets/greenlightimages/streetlight.jpg',
            striplight: 'assets/greenlightimages/striplight.jpg',
            vaportightlightimage: 'assets/greenlightimages/vaportightlightimage.png',
        },
        construction: {
            portableTowers: 'assets/greenlightimages/portabletowerimage.jpg',
            workLights: 'assets/greenlightimages/ledworklight.jpg',
            stringLights: 'assets/greenlightimages/stringlight.jpg',
            // balloonLights: '/images/construction/balloon-lights.jpg',
            auxiliary: 'assets/greenlightimages/auxilarrylightimage.jpeg',
            gantry: 'assets/greenlightimages/gantarylight.jpeg',
            handHeld: 'assets/greenlightimages/handheld.jpg'
        },
        flashlights: {
            headlamps: '/images/flashlights/headlamps.jpg',
            everydayCarry: '/images/flashlights/everyday-carry.jpg',
            keychain: '/images/flashlights/keychain.jpg',
            tactical: '/images/flashlights/tactical.jpg',
            searchlights: '/images/flashlights/searchlights.jpg',
            camping: '/images/flashlights/camping.jpg',
            chargers: '/images/flashlights/chargers.jpg'
        }
    };

    const IndustrialLightingSection = () => (
        <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 border-l-4 border-blue-600 pl-4">
                Industrial Lighting
            </h2>
            <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                Industrial lighting illuminates factories, warehouses, and other large commercial spaces.
                The primary focus is on visibility, efficiency, and safety.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {/* High Bay Lighting */}
                <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                    <div className="h-48 bg-gradient-to-br from-blue-100 to-blue-200 rounded-t-lg overflow-hidden">
                        <img 
                            src="assets/greenlightimages/highbayligt.webp" 
                            alt="High Bay Lighting"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">High Bay Lighting</h3>
                        <p className="text-gray-600 mb-4">
                            Used for ceilings 20 feet or higher. Powerful, intense light designed for large,
                            open areas like warehouses, manufacturing plants, and gyms.
                        </p>
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-gray-500">
                                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                Linear High Bay: Long, strip-like fixtures for aisles and assembly lines
                            </div>
                            <div className="flex items-center text-sm text-gray-500">
                                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                                UFO High Bay: Circular fixtures that provide concentrated light
                            </div>
                        </div>
                    </div>
                </div>

                {/* Low Bay Lighting */}
                <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                    <div className="h-48 bg-gradient-to-br from-green-100 to-green-200 rounded-t-lg overflow-hidden">
                        <img 
                            src={imagePlaceholders.industrial.lowbayligt} 
                            alt="Low Bay Lighting"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className="w-full h-full flex items-center justify-center hidden">
                            <span className="text-green-600 font-semibold">Low Bay Lighting Image</span>
                        </div>
                    </div>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">Low Bay Lighting</h3>
                        <p className="text-gray-600 mb-4">
                            Used for ceilings under 20 feet. Concentrates light in specific areas, ideal for
                            precision work in electronics or medical facilities.
                        </p>
                    </div>
                </div>

                {/* Vapor Tight Lights */}
                <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                    <div className="h-48 bg-gradient-to-br from-purple-100 to-purple-200 rounded-t-lg overflow-hidden">
                        <img 
                            src={imagePlaceholders.industrial.vaportightlightimage} 
                            alt="Vapor Tight Lights"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className="w-full h-full flex items-center justify-center hidden">
                            <span className="text-purple-600 font-semibold">Vapor Tight Lights Image</span>
                        </div>
                    </div>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">Vapor Tight Lights</h3>
                        <p className="text-gray-600 mb-4">
                            Sealed to protect against moisture, dust, and corrosive fumes. Used in harsh
                            environments like refrigerated warehouses and food processing plants.
                        </p>
                    </div>
                </div>

                {/* Explosion-Proof Lighting */}
                <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                    <div className="h-48 bg-gradient-to-br from-red-100 to-red-200 rounded-t-lg overflow-hidden">
                        <img 
                            src={imagePlaceholders.industrial.explosionprooflight} 
                            alt="Explosion-Proof Lighting"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className="w-full h-full flex items-center justify-center hidden">
                            <span className="text-red-600 font-semibold">Explosion-Proof Lighting Image</span>
                        </div>
                    </div>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">Explosion-Proof Lighting</h3>
                        <p className="text-gray-600 mb-4">
                            Designed to withstand intense heat and pressure of explosions. Used in hazardous
                            locations like mining fields, oil rigs, and chemical plants.
                        </p>
                    </div>
                </div>

                {/* Industrial Strip Lighting */}
                <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                    <div className="h-48 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-t-lg overflow-hidden">
                        <img 
                            src={imagePlaceholders.industrial.striplight} 
                            alt="Industrial Strip Lighting"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className="w-full h-full flex items-center justify-center hidden">
                            <span className="text-yellow-600 font-semibold">Strip Lighting Image</span>
                        </div>
                    </div>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">Industrial Strip Lighting</h3>
                        <p className="text-gray-600 mb-4">
                            Long, narrow fixtures that provide uniform illumination for production lines,
                            workstations, and warehouse shelving.
                        </p>
                    </div>
                </div>

                {/* Streetlights & Floodlights */}
                <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                    <div className="h-48 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-t-lg overflow-hidden">
                        <img 
                            src={imagePlaceholders.industrial.streetlight} 
                            alt="Streetlights & Floodlights"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className="w-full h-full flex items-center justify-center hidden">
                            <span className="text-indigo-600 font-semibold">Street & Flood Lights Image</span>
                        </div>
                    </div>
                    <div className="p-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">Streetlights & Floodlights</h3>
                        <p className="text-gray-600 mb-4">
                            Streetlights for industrial roads and parking lots. Floodlights illuminate wide
                            areas in parking lots, loading docks, and industrial yards.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );

    const ConstructionLightingSection = () => (
        <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 border-l-4 border-orange-600 pl-4">
                Construction Lighting
            </h2>
            <p className="text-gray-600 mb-8 text-lg leading-relaxed">
                Construction lighting is often temporary and portable, designed for durability and
                safety in harsh, changing environments.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                    {
                        title: "Portable Lighting Towers",
                        description: "Large, wheeled units with high-intensity lights on a mast to illuminate vast outdoor areas. Many use energy-efficient LED bulbs.",
                        color: "from-orange-100 to-orange-200",
                        textColor: "text-orange-600",
                        image: imagePlaceholders.construction.portableTowers
                    },
                    {
                        title: "LED Work Lights",
                        description: "Portable, durable, and energy-efficient lights for specific work areas. Available in tripods and magnetic mounts.",
                        color: "from-amber-100 to-amber-200",
                        textColor: "text-amber-600",
                        image: imagePlaceholders.construction.workLights
                    },
                    {
                        title: "String Lights (Festoon)",
                        description: "Multiple lamps on a single cable to illuminate walkways, temporary shelters, and general job site areas.",
                        color: "from-lime-100 to-lime-200",
                        textColor: "text-lime-600",
                        image: imagePlaceholders.construction.stringLights
                    },
                    // {
                    //     title: "Balloon Lights",
                    //     description: "Inflatable, diffused lights providing 360-degree, glare-free illumination over wide areas for ambient light.",
                    //     color: "from-emerald-100 to-emerald-200",
                    //     textColor: "text-emerald-600",
                    //     image: imagePlaceholders.construction.balloonLights
                    // },
                    {
                        title: "Auxiliary Lighting",
                        description: "High-visibility lighting used to highlight hazards or illuminate interior areas on construction sites.",
                        color: "from-teal-100 to-teal-200",
                        textColor: "text-teal-600",
                        image: imagePlaceholders.construction.auxiliary
                    },
                    {
                        title: "Gantry Lighting",
                        description: "Illuminates large sections of a site from higher vantage points for comprehensive coverage.",
                        color: "from-cyan-100 to-cyan-200",
                        textColor: "text-cyan-600",
                        image: imagePlaceholders.construction.gantry
                    },
                    {
                        title: "Hand-Held Lights",
                        description: "Durable flashlights and inspection spotlights for detailed work and hard-to-reach areas.",
                        color: "from-sky-100 to-sky-200",
                        textColor: "text-sky-600",
                        image: imagePlaceholders.construction.handHeld
                    }
                ].map((item, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
                        <div className={`h-48 bg-gradient-to-br ${item.color} rounded-t-lg overflow-hidden`}>
                            <img 
                                src={item.image} 
                                alt={item.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                            <div className="w-full h-full flex items-center justify-center hidden">
                                <span className={`font-semibold ${item.textColor}`}>{item.title} Image</span>
                            </div>
                        </div>
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">{item.title}</h3>
                            <p className="text-gray-600">{item.description}</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );

    const FlashlightsSection = () => (
        <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 border-l-4 border-gray-600 pl-4">
                Flashlights and Torches
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { name: "Headlamps", image: imagePlaceholders.flashlights.headlamps },
                    { name: "Everyday Carry Lights", image: imagePlaceholders.flashlights.everydayCarry },
                    { name: "Keychain Flashlights", image: imagePlaceholders.flashlights.keychain },
                    { name: "Tactical Flashlights", image: imagePlaceholders.flashlights.tactical },
                    { name: "Searchlights", image: imagePlaceholders.flashlights.searchlights },
                    { name: "Camping Flashlights", image: imagePlaceholders.flashlights.camping },
                    { name: "Charger and Batteries", image: imagePlaceholders.flashlights.chargers }
                ].map((item, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 overflow-hidden">
                        {/* <div className="h-32 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                            <img 
                                src={item.image} 
                                alt={item.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                }}
                            />
                            <div className="w-full h-full flex items-center justify-center hidden">
                                <span className="text-gray-600 font-medium text-sm text-center px-2">{item.name} Image</span>
                            </div>
                        </div> */}
                        <div className="p-4">
                            <h4 className="font-semibold text-gray-800 text-center">{item.name}</h4>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        Industrial and Construction Lights
                    </h1>
                    <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                        Industrial and construction lighting can be categorized by application, fixture type, and light source.
                        Light Emitting Diode (LED) technology is prevalent in modern industrial and construction lighting
                        due to its energy efficiency and durability.
                    </p>
                </div>

                {/* Main Content */}
                <div className="space-y-16">
                    <IndustrialLightingSection />
                    <ConstructionLightingSection />
                    <FlashlightsSection />
                </div>

                {/* Technology Highlight */}
                <div className="bg-blue-50 rounded-2xl p-8 mt-16 border border-blue-200">
                    <div className="flex flex-col md:flex-row items-center">
                        <div className="md:w-2/3">
                            <h2 className="text-2xl font-bold text-blue-900 mb-4">
                                LED Technology Advantage
                            </h2>
                            <p className="text-blue-800 text-lg">
                                Modern industrial and construction lighting heavily utilizes LED technology for its
                                superior energy efficiency, longer lifespan, durability, and environmental benefits.
                                LED lights provide better illumination while reducing maintenance costs and energy consumption.
                            </p>
                        </div>
                        <div className="md:w-1/3 mt-6 md:mt-0 flex justify-center">
                            <div className="w-32 h-32 bg-gradient-to-br from-blue-200 to-blue-300 rounded-full overflow-hidden flex items-center justify-center">
                                <img 
                                    src={imagePlaceholders.industrial.ledtechimage} 
                                    alt="LED Technology"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div className="w-full h-full flex items-center justify-center hidden">
                                    <span className="text-blue-700 font-bold text-center text-sm">LED Tech Image</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndustrialConstructionLights;